import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Axios from 'axios';
import AppLayout from '../../components/AppLayout';
import CircularLoader from '../../components/CircularLoader/CircularLoader';
import { S, Icon, formatDate } from '../regureStyles';

const MyDocumentsScreen = () => {
  const { t } = useTranslation();
  const userLogin = useSelector(state => state.userLogin);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);

  const user = userLogin?.userInfo?.data?.data || {};
  const role = user?.role || 'policyholder';
  const apiUrl = process.env.REACT_APP_BASE_URL;

  useEffect(() => {
    if (userLogin?.userInfo?.status !== 'success') return;

    const fetchDocuments = async () => {
      try {
        setLoading(true);
        const response = await Axios.get(`${apiUrl}/me/my-documents`, { withCredentials: true });
        setDocuments(response.data?.data || []);
      } catch (err) {
        setError(err.response?.data?.message || t('screens.portal.failedLoadDocuments'));
      } finally {
        setLoading(false);
      }
    };

    fetchDocuments();
  }, [userLogin, apiUrl]);

  // D-33: portal users had no download affordance — cards were plain
  // visual tiles. Hits the pre-signed URL endpoint and opens the S3
  // link in a new tab. Uses window.open so authenticated cookie context
  // isn't dragged into the S3 request; the URL is single-use, 5-min TTL.
  const handleDownload = async (docId) => {
    try {
      const response = await Axios.get(
        `${apiUrl}/me/my-documents/${docId}/download`,
        { withCredentials: true },
      );
      const url = response?.data?.data?.url;
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        setError(t('screens.portal.failedDownloadDocument'));
      }
    } catch (err) {
      setError(err.response?.data?.message || t('screens.portal.failedDownloadDocument'));
    }
  };

  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const formData = new FormData();
    for (let i = 0; i < files.length; i++) {
      formData.append('files', files[i]);
    }

    setUploading(true);
    try {
      const response = await Axios.post(`${apiUrl}/me/my-documents/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setDocuments(prev => [...prev, ...response.data?.data || []]);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || t('screens.portal.failedUploadDocuments'));
    } finally {
      setUploading(false);
    }
  };

  if (loading) return (
    <AppLayout>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <CircularLoader />
      </div>
    </AppLayout>
  );

  return (
    <AppLayout>
      <div style={{
        flex: 1,
        overflowY: 'auto',
        background: `linear-gradient(135deg, ${S.bg} 0%, #f8fafc 100%)`,
        padding: '32px 40px',
        minHeight: '100%'
      }}>
        {/* Page Header */}
        <div style={{ marginBottom: 32 }}>
          <div style={{ fontSize: 12, color: S.textMuted, marginBottom: 8, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            <span style={{ color: S.burgundy, fontWeight: 700 }}>{t('screens.portal.breadcrumbPortal')}</span> / {t('screens.portal.breadcrumbMyDocuments')}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 28, fontWeight: 800, color: S.textPrimary, letterSpacing: '-0.5px' }}>
                {t('screens.portal.myDocuments')}
              </div>
              <div style={{ fontSize: 14, color: S.textMuted, marginTop: 6 }}>
                {role === 'broker' ? t('screens.portal.documentsSubtitleBroker') : t('screens.portal.documentsSubtitlePolicyholder')}
              </div>
            </div>
            <label
              style={{
                background: S.burgundy,
                color: '#fff',
                border: 'none',
                padding: '10px 20px',
                borderRadius: 10,
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                boxShadow: '0 2px 8px rgba(128, 0, 32, 0.15)',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.background = '#5c001a';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(128, 0, 32, 0.25)';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = S.burgundy;
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(128, 0, 32, 0.15)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <Icon name="upload" color="#fff" size={16} />
              {t('screens.portal.uploadDocument')}
              <input
                type="file"
                multiple
                onChange={handleFileUpload}
                disabled={uploading}
                style={{ display: 'none' }}
              />
            </label>
          </div>
        </div>

        {/* Messages */}
        {error && (
          <div style={{
            background: 'rgba(220, 38, 38, 0.08)',
            color: '#dc2626',
            padding: '14px 18px',
            borderRadius: 10,
            marginBottom: 20,
            fontSize: 13,
            border: '1px solid rgba(220, 38, 38, 0.15)'
          }}>
            {error}
          </div>
        )}

        {uploading && (
          <div style={{
            background: 'rgba(37, 99, 235, 0.08)',
            color: '#2563eb',
            padding: '14px 18px',
            borderRadius: 10,
            marginBottom: 20,
            fontSize: 13,
            border: '1px solid rgba(37, 99, 235, 0.15)'
          }}>
            {t('screens.portal.uploadingDocuments')}
          </div>
        )}

        {/* Documents Grid */}
        {documents.length === 0 ? (
          <div style={{
            background: '#fff',
            border: `1px dashed ${S.borderLight}`,
            borderRadius: 14,
            padding: 60,
            textAlign: 'center',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
          }}>
            <Icon name="file" color={S.borderLight} size={48} style={{ marginBottom: 16, opacity: 0.5 }} />
            <div style={{ fontSize: 16, fontWeight: 600, color: S.textPrimary, marginBottom: 8 }}>
              {t('screens.portal.noDocumentsYet')}
            </div>
            <div style={{ fontSize: 14, color: S.textMuted }}>
              {role === 'broker' ? t('screens.portal.noDocumentsHintBroker') : t('screens.portal.noDocumentsHintPolicyholder')}
            </div>
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
            gap: 20
          }}>
            {documents.map((doc, idx) => (
              <div
                key={doc._id || idx}
                style={{
                  background: '#fff',
                  border: `1px solid ${S.borderLight}`,
                  borderRadius: 12,
                  padding: 16,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  flexDirection: 'column',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                  e.currentTarget.style.transform = 'translateY(-4px)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                  <Icon name="file-text" color={S.burgundy} size={24} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: S.textPrimary,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}>
                      {doc.originalName || doc.name || doc.fileName || t('screens.portal.documentFallback')}
                    </div>
                  </div>
                </div>
                <div style={{ fontSize: 12, color: S.textMuted, marginBottom: 8 }}>
                  {doc.linkedTo ? (
                    <span>{doc.linkedTo.type}: {doc.linkedTo.name}</span>
                  ) : (
                    <span>{t('screens.portal.uploaded')}</span>
                  )}
                </div>
                <div style={{ fontSize: 11, color: S.textMuted, marginBottom: 12 }}>
                  {formatDate(doc.uploadedAt)}
                </div>
                <button
                  type='button'
                  onClick={() => handleDownload(doc._id)}
                  disabled={!doc._id}
                  style={{
                    marginTop: 'auto',
                    background: '#fff',
                    color: S.burgundy,
                    border: `1px solid ${S.burgundy}`,
                    borderRadius: 8,
                    padding: '8px 12px',
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: doc._id ? 'pointer' : 'not-allowed',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 6,
                  }}
                  onMouseEnter={e => {
                    if (!doc._id) return;
                    e.currentTarget.style.background = S.burgundy;
                    e.currentTarget.style.color = '#fff';
                  }}
                  onMouseLeave={e => {
                    if (!doc._id) return;
                    e.currentTarget.style.background = '#fff';
                    e.currentTarget.style.color = S.burgundy;
                  }}
                >
                  <Icon name='download' color='currentColor' size={14} />
                  {t('screens.portal.download')}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  );
};

export default MyDocumentsScreen;
